// BirthDataForm - Multi-step wizard for entering birth data
// Animated, user-friendly, and beautifully designed
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, MapPin, Sparkles, ChevronRight, ChevronLeft, Search, Check, HelpCircle } from 'lucide-react';
import { Button } from '../common/Button';
import { ZODIAC_SIGNS } from '../../utils/astroConstants';

const SUN_SIGN_STARTS = [
    { month: 1, day: 20, signIndex: 10 }, // Aquarius
    { month: 2, day: 19, signIndex: 11 }, // Pisces
    { month: 3, day: 21, signIndex: 0 },  // Aries
    { month: 4, day: 20, signIndex: 1 },  // Taurus
    { month: 5, day: 21, signIndex: 2 },  // Gemini
    { month: 6, day: 21, signIndex: 3 },  // Cancer
    { month: 7, day: 23, signIndex: 4 },  // Leo
    { month: 8, day: 23, signIndex: 5 },  // Virgo
    { month: 9, day: 23, signIndex: 6 },  // Libra
    { month: 10, day: 23, signIndex: 7 }, // Scorpio
    { month: 11, day: 22, signIndex: 8 }, // Sagittarius
    { month: 12, day: 22, signIndex: 9 }, // Capricorn
];

export const getApproximateSunSignFromDate = (birthDate) => {
    if (!birthDate) return null;

    const [, month, day] = birthDate.split('-').map(Number);
    if (!month || !day || month < 1 || month > 12 || day < 1 || day > 31) return null;

    let signIndex = 9; // Capricorn covers Jan 1-19 before Aquarius starts.
    for (const start of SUN_SIGN_STARTS) {
        if (month > start.month || (month === start.month && day >= start.day)) {
            signIndex = start.signIndex;
        }
    }

    return ZODIAC_SIGNS[signIndex];
};

// Step indicator component
const StepIndicator = ({ currentStep, totalSteps }) => (
    <div className="flex justify-center gap-2 mb-8">
        {Array.from({ length: totalSteps }).map((_, i) => (
            <motion.div
                key={i}
                className={`w-3 h-3 rounded-full transition-colors ${i === currentStep
                        ? 'bg-amber-400 shadow-lg shadow-amber-400/50'
                        : i < currentStep
                            ? 'bg-purple-500'
                            : 'bg-gray-600'
                    }`}
                animate={{ scale: i === currentStep ? 1.2 : 1 }}
                transition={{ type: 'spring', stiffness: 300 }}
            />
        ))}
    </div>
);

// Date step
const DateStep = ({ birthDate, setBirthDate, onNext }) => {
    // Get zodiac sign preview
    const zodiacPreview = getApproximateSunSignFromDate(birthDate);

    return (
        <motion.div
            className="space-y-6"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
        >
            <div className="text-center mb-8">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-amber-400" />
                <h2 className="text-2xl font-serif text-amber-100">When were you born?</h2>
                <p className="text-purple-300/70 mt-2">Enter your birth date</p>
            </div>

            <div className="relative">
                <input
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    className="w-full px-4 py-4 bg-purple-900/30 border border-purple-500/30 rounded-xl text-amber-100 text-lg focus:outline-none focus:border-amber-400/50 focus:ring-2 focus:ring-amber-400/20 transition-all text-center"
                    max={new Date().toISOString().split('T')[0]}
                />
            </div>

            {/* Zodiac preview */}
            <AnimatePresence>
                {zodiacPreview && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="text-center p-4 bg-gradient-to-r from-purple-900/30 via-purple-800/20 to-purple-900/30 rounded-xl border border-purple-500/20"
                    >
                        <span className="text-3xl" style={{ textShadow: `0 0 20px ${zodiacPreview.color}` }}>
                            {zodiacPreview.symbol}
                        </span>
                        <div className="mt-2 text-amber-200">Sun in <strong>{zodiacPreview.name}</strong></div>
                        <div className="text-sm text-purple-300/70">{zodiacPreview.element} Sign</div>
                    </motion.div>
                )}
            </AnimatePresence>

            <Button
                variant="primary"
                size="lg"
                onClick={onNext}
                disabled={!birthDate}
                className="w-full"
                icon={ChevronRight}
            >
                Continue
            </Button>
        </motion.div>
    );
};

// Time step
const TimeStep = ({ birthTime, setBirthTime, unknownTime, setUnknownTime, onNext, onBack }) => (
    <motion.div
        className="space-y-6"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -50 }}
    >
        <div className="text-center mb-8">
            <Clock className="w-12 h-12 mx-auto mb-4 text-amber-400" />
            <h2 className="text-2xl font-serif text-amber-100">What time were you born?</h2>
            <p className="text-purple-300/70 mt-2">This determines your Rising Sign</p>
        </div>

        <div className="relative">
            <input
                type="time"
                value={birthTime}
                onChange={(e) => setBirthTime(e.target.value)}
                disabled={unknownTime}
                className={`w-full px-4 py-4 bg-purple-900/30 border border-purple-500/30 rounded-xl text-amber-100 text-lg focus:outline-none focus:border-amber-400/50 focus:ring-2 focus:ring-amber-400/20 transition-all text-center ${unknownTime ? 'opacity-50' : ''}`}
            />
        </div>

        <label className="flex items-center justify-center gap-3 cursor-pointer group">
            <div
                className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${unknownTime
                        ? 'bg-purple-600 border-purple-400'
                        : 'border-purple-500/50 group-hover:border-purple-400'
                    }`}
                onClick={() => setUnknownTime(!unknownTime)}
            >
                {unknownTime && <Check className="w-4 h-4 text-white" />}
            </div>
            <span className="text-purple-200">I don't know my exact birth time</span>
            <HelpCircle className="w-4 h-4 text-purple-400/50" />
        </label>

        {unknownTime && (
            <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="text-center p-3 bg-amber-900/20 border border-amber-500/20 rounded-lg text-amber-200/80 text-sm"
            >
                ✨ We'll use 12:00 noon. Your Rising Sign may not be accurate, but Sun & Moon placements will be.
            </motion.div>
        )}

        <div className="flex gap-3">
            <Button
                variant="secondary"
                size="lg"
                onClick={onBack}
                className="flex-1"
                icon={ChevronLeft}
            >
                Back
            </Button>
            <Button
                variant="primary"
                size="lg"
                onClick={onNext}
                disabled={!unknownTime && !birthTime}
                className="flex-1"
                icon={ChevronRight}
            >
                Continue
            </Button>
        </div>
    </motion.div>
);

// Location step
const LocationStep = ({
    birthLocation,
    setBirthLocation,
    locationResults,
    isSearchingLocation,
    onSearch,
    onSelectLocation,
    onNext,
    onBack
}) => {
    const [searchQuery, setSearchQuery] = useState('');

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchQuery.length >= 2) {
                onSearch(searchQuery);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery, onSearch]);

    return (
        <motion.div
            className="space-y-6"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
        >
            <div className="text-center mb-8">
                <MapPin className="w-12 h-12 mx-auto mb-4 text-amber-400" />
                <h2 className="text-2xl font-serif text-amber-100">Where were you born?</h2>
                <p className="text-purple-300/70 mt-2">Search for your birth city</p>
            </div>

            <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Enter city name..."
                    className="w-full pl-12 pr-4 py-4 bg-purple-900/30 border border-purple-500/30 rounded-xl text-amber-100 placeholder-purple-400/50 focus:outline-none focus:border-amber-400/50 focus:ring-2 focus:ring-amber-400/20 transition-all"
                />
                {isSearchingLocation && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                            className="w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full"
                        />
                    </div>
                )}
            </div>

            {/* Search results */}
            <AnimatePresence>
                {locationResults.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar"
                    >
                        {locationResults.map((loc, i) => (
                            <motion.button
                                key={i}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.05 }}
                                onClick={() => {
                                    onSelectLocation(loc);
                                    setSearchQuery(loc.displayName.split(',')[0]);
                                }}
                                className="w-full text-left px-4 py-3 bg-purple-900/40 hover:bg-purple-800/50 border border-purple-500/20 hover:border-purple-400/40 rounded-lg transition-all group"
                            >
                                <div className="flex items-center gap-3">
                                    <MapPin className="w-4 h-4 text-purple-400 group-hover:text-amber-400 transition-colors" />
                                    <span className="text-amber-100 text-sm truncate">
                                        {loc.displayName}
                                    </span>
                                </div>
                            </motion.button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Selected location */}
            {birthLocation && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-4 bg-green-900/20 border border-green-500/30 rounded-xl"
                >
                    <div className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-green-400" />
                        <span className="text-green-200 text-sm truncate">{birthLocation}</span>
                    </div>
                </motion.div>
            )}

            <div className="flex gap-3">
                <Button
                    variant="secondary"
                    size="lg"
                    onClick={onBack}
                    className="flex-1"
                    icon={ChevronLeft}
                >
                    Back
                </Button>
                <Button
                    variant="primary"
                    size="lg"
                    onClick={onNext}
                    disabled={!birthLocation}
                    className="flex-1"
                    icon={ChevronRight}
                >
                    Continue
                </Button>
            </div>
        </motion.div>
    );
};

// Confirm step
const ConfirmStep = ({ birthDate, birthTime, unknownTime, birthLocation, onCalculate, onBack, isLoading }) => {
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return unknownTime ? '12:00 PM (estimated)' : '';
        const [h, m] = timeStr.split(':');
        const hour = parseInt(h);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        return `${displayHour}:${m} ${ampm}`;
    };

    return (
        <motion.div
            className="space-y-6"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
        >
            <div className="text-center mb-8">
                <Sparkles className="w-12 h-12 mx-auto mb-4 text-amber-400" />
                <h2 className="text-2xl font-serif text-amber-100">Ready to Unveil Your Stars?</h2>
                <p className="text-purple-300/70 mt-2">Review your cosmic coordinates</p>
            </div>

            <div className="space-y-4">
                <div className="p-4 bg-purple-900/30 border border-purple-500/20 rounded-xl">
                    <div className="flex items-center gap-3 text-purple-300/70 text-sm mb-1">
                        <Calendar className="w-4 h-4" /> Birth Date
                    </div>
                    <div className="text-amber-100 font-medium">{formatDate(birthDate)}</div>
                </div>

                <div className="p-4 bg-purple-900/30 border border-purple-500/20 rounded-xl">
                    <div className="flex items-center gap-3 text-purple-300/70 text-sm mb-1">
                        <Clock className="w-4 h-4" /> Birth Time
                    </div>
                    <div className="text-amber-100 font-medium">{formatTime(birthTime)}</div>
                </div>

                <div className="p-4 bg-purple-900/30 border border-purple-500/20 rounded-xl">
                    <div className="flex items-center gap-3 text-purple-300/70 text-sm mb-1">
                        <MapPin className="w-4 h-4" /> Birth Location
                    </div>
                    <div className="text-amber-100 font-medium text-sm">{birthLocation}</div>
                </div>
            </div>

            <div className="flex gap-3">
                <Button
                    variant="secondary"
                    size="lg"
                    onClick={onBack}
                    className="flex-1"
                    icon={ChevronLeft}
                    disabled={isLoading}
                >
                    Back
                </Button>
                <Button
                    variant="primary"
                    size="lg"
                    onClick={onCalculate}
                    loading={isLoading}
                    className="flex-1"
                    icon={Sparkles}
                >
                    {isLoading ? 'Calculating...' : 'Reveal My Chart'}
                </Button>
            </div>
        </motion.div>
    );
};

// Main form component
export const BirthDataForm = ({
    birthDate, setBirthDate,
    birthTime, setBirthTime,
    birthLocation,
    unknownTime, setUnknownTime,
    locationResults,
    isSearchingLocation,
    searchLocation,
    selectLocation,
    currentStep,
    nextStep,
    prevStep,
    isLoading,
    onCalculate
}) => {
    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return (
                    <DateStep
                        birthDate={birthDate}
                        setBirthDate={setBirthDate}
                        onNext={nextStep}
                    />
                );
            case 1:
                return (
                    <TimeStep
                        birthTime={birthTime}
                        setBirthTime={setBirthTime}
                        unknownTime={unknownTime}
                        setUnknownTime={setUnknownTime}
                        onNext={nextStep}
                        onBack={prevStep}
                    />
                );
            case 2:
                return (
                    <LocationStep
                        birthLocation={birthLocation}
                        locationResults={locationResults}
                        isSearchingLocation={isSearchingLocation}
                        onSearch={searchLocation}
                        onSelectLocation={selectLocation}
                        onNext={nextStep}
                        onBack={prevStep}
                    />
                );
            case 3:
                return (
                    <ConfirmStep
                        birthDate={birthDate}
                        birthTime={birthTime}
                        unknownTime={unknownTime}
                        birthLocation={birthLocation}
                        onCalculate={onCalculate}
                        onBack={prevStep}
                        isLoading={isLoading}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="max-w-md mx-auto">
            <StepIndicator currentStep={currentStep} totalSteps={4} />
            <AnimatePresence mode="wait">
                {renderStep()}
            </AnimatePresence>
        </div>
    );
};

export default BirthDataForm;
